<template>
  <Transition name="fade">
    <div 
      v-if="isPageLoading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm"
    >
      <div class="flex flex-col items-center space-y-6 px-4">
        <!-- Simplified Modern Spinner -->
        <div class="relative w-16 h-16">
          <!-- Spinning ring -->
          <div class="absolute inset-0 rounded-full spinner-ring"></div>
        </div>
        
        <!-- Loading Text -->
        <div class="text-center space-y-3">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ loadingText }}
          </h3>
          
          <!-- Progress bar -->
          <div class="w-64 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div class="h-full rounded-full animate-loading-bar progress-gradient"></div>
          </div>
          
          <!-- Subtle hint text -->
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Please wait...
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useLoadingStore } from '@/stores/loading'

// Store
const loadingStore = useLoadingStore()

// Computed Properties
const isPageLoading = computed(() => loadingStore.isPageLoading)
const loadingText = computed(() => loadingStore.loadingText)
</script>

<style scoped>
/* Fade transition for smooth entry/exit */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Simplified spinner ring with gradient */
.spinner-ring {
  border: 4px solid transparent;
  border-top: 4px solid #009ada;
  border-right: 4px solid #2ec2ff;
  border-bottom: 4px solid #25b03f;
  animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.dark .spinner-ring {
  border-top: 4px solid #2ec2ff;
  border-right: 4px solid #62d6ff;
  border-bottom: 4px solid #78d539;
}

/* Spin animation */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Progress bar gradient */
.progress-gradient {
  background: linear-gradient(90deg, #009ada 0%, #2ec2ff 50%, #25b03f 100%);
}

.dark .progress-gradient {
  background: linear-gradient(90deg, #2ec2ff 0%, #62d6ff 50%, #78d539 100%);
}

/* Loading bar animation */
@keyframes loading-bar {
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-loading-bar {
  animation: loading-bar 2s ease-in-out infinite;
}
</style>
